import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CourseContext } from '@/modules/RegisterLayout/contexts/CoursesContextProvider';
import { RegistrationContext } from '@/modules/RegisterLayout/contexts/RegistrationContextProvider';
import { UserDetailsContext } from '@/modules/RegisterLayout/contexts/UserDetailsContextProvider';
import FooterPortal from '@/modules/common/Portals/FooterPortal/FooterPortal';
import { useApplyCoupon } from '@/modules/RegisterLayout/hooks/useApplyCoupon';
import { useInitiatePayment } from '@/modules/RegisterLayout/hooks/useInitiatePayment';
import { useLeadCapture } from '@/modules/RegisterLayout/hooks/useLeadCapture';
import { useVerifyPayment } from '@/modules/RegisterLayout/hooks/useVerifyPayment';
import { dataLayer } from '@/utils/dataLayer';
import { razorPayKey } from '@/utils/env.constants';
import { Checkbox, InputField, Typography, useToast } from '@pw-tech/omni-ui';
import useIsMobile from '@/hooks/useIsMobile';
import { CheckCircle2, X } from 'lucide-react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import PaymentStatusModal from './PaymentStatusModal/PaymentStatusModal';
import { getErrorMessage, getNextStep } from '@/modules/common/utils';
import AdmissionClosedModal from '@/modules/RegisterLayout/Modals/AdmissionClosedModal/AdmissionClosedModal';
const RegistrationFee = () => {
  // States
  const [couponCode, setCouponCode] = useState('');
  const [couponCodeApplied, setCouponCodeApplied] = useState<boolean>(false);
  const [priceAfterCouponCode, setPriceAfterCouponCode] = useState<number>(0);
  const [isDeclarationChecked, setIsDeclarationChecked] = useState<boolean>(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState({
    success: false,
    failed: false
  });
  const [paymentProgress, setPaymentProgress] = useState(false);
  const [uenNumber, setUenNumber] = useState('');
  const [showAdmissionClosedDialog, setShowAdmissionClosedDialog] = useState(false);

  // Hooks
  const router = useRouter();
  const {
    courseId,
    coming_from_page,
    section
  } = router.query;
  const {
    mutate,
    isLoading
  } = useApplyCoupon();
  const {
    mutateAsync: asyncLeadCapture
  } = useLeadCapture();
  const {
    mutateAsync: asyncInitiatePayment,
    isPending: isInitiatingPayment
  } = useInitiatePayment();
  const {
    mutateAsync: asyncVerifyPayment,
    isPending: isVerifyingPayment
  } = useVerifyPayment();

  // Context
  const {
    registrationDetails,
    isFormSubmitting,
    submitRegisterForm
  } = useContext(RegistrationContext);
  const {
    selectedCourseData
  } = useContext(CourseContext);
  const {
    user
  } = useContext(UserDetailsContext);
  const {
    toast: showToaster
  } = useToast();
  const isMobileScreen = useIsMobile();
  const utmQuery = JSON.parse(sessionStorage.getItem('query') || '{}');
  const isSohCourse = useMemo(() => {
    return selectedCourseData?.crmProductName?.includes('BVoc');
  }, [selectedCourseData]);
  const uponFeePaymentAccess = useMemo(() => {
    let title = '';
    let contentTitle = '';
    let content = [];
    if (isSohCourse) {
      title = "You're eligible for direct admission to PW Institute of Innovation!";
      contentTitle = 'Once your payment is completed, you’ll move to the next steps:';
      content = ['Keep your academic documents ready for verification.', 'Campus will be allocated based on your academic performance.', "You're now on a fast-track admission process."];
    } else if (selectedCourseData?.skipCeeExam) {
      title = "You're now eligible to apply for the MBA Program at PW Institute of Innovation (PW IOI).";
      contentTitle = 'Upon fee payment, you will get access to:';
      content = ['MBA Selection Portal: Access this portal to track your application status and upload academic, personal, and professional documents.', 'Personal Interview (PI) Slot: The PW IOI Admissions Team will prioritize scheduling your interaction with IOI faculty and mentors upon shortlisting.'];
    } else if (registrationDetails?.isEligibleForDirectAdmission) {
      title = 'Your Application is Eligible for Fast Track Process to PW Institute of Innovation!';
      contentTitle = 'Upon successful payment, you will gain access to the PW IOI Fast Track Process and receive a detailed syllabus to help you prepare effectively.';
      content = ['Get access to the PW IOI Fast Track Process', 'Detailed syllabus to help you prepare better', 'Practice mock test to assess your readiness before the interview'];
    } else {
      title = "You're now eligible for CEE 2026, your gateway to PW Institute of Innovation (PW IOI).";
      contentTitle = 'Upon fee payment, you will get access to:';
      content = ['Get Access to PW CEE Exam', 'Detailed syllabus and sample papers to help you prepare better', 'Practice mock test to assess your readiness before the exam'];
    }
    return {
      title,
      contentTitle,
      content
    };
  }, [registrationDetails?.isEligibleForDirectAdmission, isSohCourse, selectedCourseData?.skipCeeExam]);
  const handleCouponCodeApply = () => {
    mutate({
      coupon: couponCode
    }, {
      onSuccess: (data: any) => {
        setPriceAfterCouponCode(data?.data?.courseDetails?.afterCouponPrice);
        setCouponCodeApplied(true);
      },
      onError: (error: any) => {
        const errorMessage = getErrorMessage(error, 'Error in applying coupon code');
        showToaster({
          message: errorMessage,
          variant: 'error',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center'
          }
        });
      }
    });
  };
  const handleCouponCodeRemove = () => {
    setCouponCodeApplied(false);
    setPriceAfterCouponCode(0);
    setCouponCode('');
  };
  const handlePayment = async () => {
    // To handle tab close
    dataLayer({
      event: 'ioi_proceed_to_pay',
      application_type: 'cee',
      coming_from_page: coming_from_page ?? '',
      section: section ?? '',
      course_selected: selectedCourseData?.name ?? '',
      ...utmQuery
    });

    // Capture Lead
    asyncLeadCapture();
    const isAdmissionOpen = selectedCourseData?.isCurrent;
    if (isAdmissionOpen === false) {
      setShowAdmissionClosedDialog(true);
      return;
    }

    // Initiate Payment
    asyncInitiatePayment({
      coupon: couponCode || ''
    }, {
      onSuccess: (paymentData: any) => {
        const orderResponse = paymentData?.data;
        const data = {
          key: razorPayKey,
          amount: orderResponse?.courseDetails?.price,
          currency: 'INR',
          order_id: orderResponse?.orderId,
          name: `Physics Wallah Pvt. Ltd.`,
          prefill: {
            name: `${user?.firstName} ${user?.lastName}`,
            email: registrationDetails?.email,
            contact: `${user?.isdCode}${user?.phoneNumber}`
          },
          handler: async (response: any) => {
            try {
              asyncVerifyPayment({
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                signature: response.razorpay_signature
              }, {
                onSuccess: (paymentResponse: any) => {
                  dataLayer({
                    user_id: user?._id,
                    user_name: `${user?.firstName} ${user?.lastName}`,
                    email: registrationDetails?.email,
                    mobile_number: `${user?.isdCode}${user?.phoneNumber}`,
                    // school_name: activeBatch?.school?.name,
                    // course_name: activeBatch?.name,
                    payment_status: 'success',
                    order_id: response.razorpay_order_id,
                    amount: orderResponse?.courseDetails?.price,
                    currency: 'INR',
                    event: 'payment_status',
                    ...utmQuery
                  });
                  dataLayer({
                    event: 'ioi_application_payment_status',
                    ioi_proceed_to_pay: 'yes',
                    payment_status: 'success',
                    coming_from_page: coming_from_page ?? '',
                    section: section ?? '',
                    course_selected: selectedCourseData?.name ?? '',
                    application_type: 'cee',
                    ...utmQuery
                  });
                  asyncLeadCapture();
                  setUenNumber(paymentResponse?.data?.uniqueEnrollmentNumber || '');
                  setPaymentModalOpen({
                    ...paymentModalOpen,
                    success: true
                  });
                  setPaymentProgress(false);
                },
                onError: (error: any) => {
                  dataLayer({
                    event: 'ioi_application_payment_status',
                    ioi_proceed_to_pay: 'yes',
                    payment_status: 'failed',
                    coming_from_page: coming_from_page ?? '',
                    section: section ?? '',
                    course_selected: selectedCourseData?.name ?? '',
                    application_type: 'cee',
                    ...utmQuery
                  });
                  dataLayer({
                    user_id: user?._id,
                    user_name: `${user?.firstName} ${user?.lastName}`,
                    email: registrationDetails?.email,
                    mobile_number: `${user?.isdCode}${user?.phoneNumber}`,
                    // school_name: activeBatch?.school?.name,
                    // course_name: activeBatch?.name,
                    payment_status: 'fail',
                    order_id: response.razorpay_order_id,
                    amount: orderResponse?.courseDetails?.price,
                    currency: 'INR',
                    event: 'payment_status',
                    ...utmQuery
                  });
                  setPaymentProgress(false);
                  asyncLeadCapture();
                }
              });
            } catch (error: any) {
              dataLayer({
                event: 'ioi_application_payment_status',
                ioi_proceed_to_pay: 'yes',
                payment_status: 'failed',
                coming_from_page: coming_from_page ?? '',
                section: section ?? '',
                course_selected: selectedCourseData?.name ?? '',
                application_type: 'cee',
                ...utmQuery
              });
              dataLayer({
                user_id: user?._id,
                user_name: `${user?.firstName} ${user?.lastName}`,
                email: registrationDetails?.email,
                mobile_number: `${user?.isdCode}${user?.phoneNumber}`,
                // school_name: activeBatch?.school?.name,
                // course_name: activeBatch?.name,
                payment_status: 'fail',
                order_id: response.razorpay_order_id,
                amount: orderResponse?.courseDetails?.price,
                currency: 'INR',
                event: 'payment_status',
                ...utmQuery
              });
              setPaymentProgress(false);
            }
          }
        };
        const razorpay = new window.Razorpay(data);
        razorpay?.open();
        razorpay?.on('payment.failed', (response: any) => {
          setPaymentModalOpen({
            ...paymentModalOpen,
            failed: true
          });
          asyncLeadCapture();
          dataLayer({
            user_id: user?._id,
            user_name: `${user?.firstName} ${user?.lastName}`,
            email: registrationDetails?.email || user?.email,
            mobile_number: `${user?.isdCode}${user?.phoneNumber}`,
            // school_name: activeBatch?.school?.name,
            course_name: selectedCourseData?.name,
            payment_status: 'fail',
            order_id: response.error.metadata.order_id,
            amount: orderResponse?.courseDetails?.price,
            currency: 'INR',
            event: 'payment_status',
            ...utmQuery
          });
          dataLayer({
            event: 'ioi_application_payment_status',
            ioi_proceed_to_pay: 'yes',
            payment_status: 'failed',
            coming_from_page: coming_from_page ?? '',
            section: section ?? '',
            course_selected: selectedCourseData?.name ?? '',
            application_type: 'cee',
            ...utmQuery
          });
        });
        setPaymentProgress(false);
      },
      onError: (error: any) => {
        setPaymentProgress(false);
      }
    });
  };
  const handleContinue = async () => {
    if (paymentModalOpen?.failed) {
      setPaymentModalOpen({
        success: false,
        failed: false
      });
      return;
    }
    await submitRegisterForm({
      step: getNextStep(router.query.step, registrationDetails?.step)
    }, true);
  };
  return <div data-sentry-component="RegistrationFee" data-sentry-source-file="RegistrationFee.tsx">
      <div className="flex flex-col gap-6">
        <Typography variant="regular" className="bg-[#FFF6E5] md:!items-center flex gap-2 p-4 rounded-lg" data-sentry-element="Typography" data-sentry-source-file="RegistrationFee.tsx">
          {isMobileScreen ? <div className="flex gap-2 ">
              <Image src="/images/registerPage/trophy.png" alt="trophy-icon" className="!h-8 !w-8 rounded-full" width={28} height={28} />
              <div className="flex">
                <p className="font-semibold">
                  Bravo!&nbsp;{' '}
                  <span className="font-normal">{uponFeePaymentAccess?.title || ''}</span>
                </p>
              </div>
            </div> : <>
              <div className="">
                <Image src="/images/registerPage/trophy.png" alt="trophy-icon" className="items-start rounded-full" width={28} height={28} />
              </div>
              <div className="flex flex-wrap">
                <span className="font-semibold">Bravo!&nbsp;</span>
                <span>{uponFeePaymentAccess?.title}</span>
              </div>
            </>}
        </Typography>
        <Typography variant="heading3" weight="semi-bold" className="!text-2xl" data-sentry-element="Typography" data-sentry-source-file="RegistrationFee.tsx">
          Application Fee
        </Typography>
        <div>
          <Typography component="div" variant="regular" className="!text-[#757575] !text-lg pb-3" data-sentry-element="Typography" data-sentry-source-file="RegistrationFee.tsx">
            {uponFeePaymentAccess?.contentTitle ?? ''}
          </Typography>
          {uponFeePaymentAccess?.content?.map((item, index) => <div className="flex items-center gap-2 py-1" key={item}>
              <CheckCircle2 size={20} className="" color="#099250" />
              <Typography component="div" variant="regular" className="!text-[#3D3D3D]" key={item}>
                {item}
              </Typography>
            </div>)}

          <Typography variant="heading4" weight="semi-bold" className="mb-2 mt-9" data-sentry-element="Typography" data-sentry-source-file="RegistrationFee.tsx">
            Have a coupon code?
          </Typography>
          <div className="flex items-center gap-2">
            <InputField label="Enter Code" placeholder="Enter Code" value={couponCode} onChange={e => setCouponCode(e.target.value?.toUpperCase())} type="text" disabled={isLoading || paymentProgress} postIcon={couponCodeApplied ? <Typography variant="regular" className="!text-red-600 cursor-pointer hover:!text-blue-600 hover:underline" onClick={handleCouponCodeRemove}>
                    Remove
                  </Typography> : couponCode ? <Typography variant="regular" className="!text-blue-600 cursor-pointer hover:underline" onClick={handleCouponCodeApply}>
                    APPLY
                  </Typography> : null} data-sentry-element="InputField" data-sentry-source-file="RegistrationFee.tsx" />
          </div>
          <div className="relative flex items-center gap-2 py-3 w-fit">
            {couponCodeApplied ? <>
                <Image src="/images/registerPage/coupon-code-success.gif" alt="success-gif" className="absolute top-[-20px] left-0 rounded-full" width={100} height={100} />
                <Image src="/images/registerPage/coupon-code-success.gif" alt="success-gif" className="absolute right-0 top-[-20px] rounded-full" width={100} height={100} />
              </> : null}
            <Typography variant="regular" data-sentry-element="Typography" data-sentry-source-file="RegistrationFee.tsx">Total Fee </Typography>
            <Typography className="" weight="semi-bold" variant="heading3" data-sentry-element="Typography" data-sentry-source-file="RegistrationFee.tsx">
              ₹
              {couponCodeApplied ? priceAfterCouponCode?.toString() : selectedCourseData?.metaData?.applicationFee || ''}
            </Typography>
          </div>

          <div className="h-8" />
          {/* Footer Portal */}
          <div className="py-2">
            <FooterPortal label={paymentProgress || isFormSubmitting || isInitiatingPayment || isVerifyingPayment ? 'Loading...' : `Click to Pay ₹${couponCodeApplied ? priceAfterCouponCode?.toString() : selectedCourseData?.metaData?.applicationFee || ''}`} type="button" disabled={paymentProgress || isInitiatingPayment || isVerifyingPayment} isFormSubmitting={paymentProgress || isFormSubmitting || isInitiatingPayment || isVerifyingPayment} btnWrapperClass="relative sm:left-[30%]" onClick={handlePayment} data-sentry-element="FooterPortal" data-sentry-source-file="RegistrationFee.tsx" />
          </div>
        </div>
      </div>

      {/* Payment Status Modal */}
      <PaymentStatusModal isOpen={paymentModalOpen?.success || paymentModalOpen?.failed} type={paymentModalOpen?.success ? 'success' : paymentModalOpen?.failed ? 'failed' : null} onClose={() => {
      setPaymentModalOpen({
        success: false,
        failed: false
      });
    }} showUenNumber uenNumber={uenNumber} onContinue={handleContinue} data-sentry-element="PaymentStatusModal" data-sentry-source-file="RegistrationFee.tsx" />

      {/* Admission Closed Dialog */}
      <AdmissionClosedModal isOpen={showAdmissionClosedDialog} onClose={() => {
      setShowAdmissionClosedDialog(false);
    }} data-sentry-element="AdmissionClosedModal" data-sentry-source-file="RegistrationFee.tsx" />
    </div>;
};
export default RegistrationFee;